/**
 * BPHistoryList - Display list of BP readings with pagination
 *
 * Features:
 * - Fetches and displays user's BP readings
 * - Sorted by date descending (most recent first)
 * - Pagination support
 * - Edit and delete functionality with confirmation
 * - Empty state when no readings
 * - Loading state
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getBPReadings, deleteBPReading } from '../../services/api';
import BPReadingCard from './BPReadingCard';
import type { BPReading } from '../../types/bloodPressure';

interface BPHistoryListProps {
  days?: number;
  limit?: number;
  onEdit?: (reading: BPReading) => void;
}

const BPHistoryList: React.FC<BPHistoryListProps> = ({
  days = 30,
  limit = 20,
  onEdit
}) => {
  const [offset, setOffset] = useState(0);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Fetch readings
  const { data, isLoading, error } = useQuery({
    queryKey: ['bp-readings', days, limit, offset],
    queryFn: () => getBPReadings({ days, limit, offset }),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: deleteBPReading,
    onSuccess: () => {
      // Invalidate queries to refetch data
      queryClient.invalidateQueries({ queryKey: ['bp-readings'] });
      queryClient.invalidateQueries({ queryKey: ['bp-readings-today'] });
      queryClient.invalidateQueries({ queryKey: ['bp-trends'] });
      setDeleteConfirmId(null);
    },
  });

  const handleDelete = (readingId: string) => {
    setDeleteConfirmId(readingId);
  };

  const confirmDelete = () => {
    if (deleteConfirmId) {
      deleteMutation.mutate(deleteConfirmId);
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmId(null);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card bg-red-50 border-l-4 border-red-400">
        <p className="text-sm text-red-800">
          Failed to load BP readings. Please try again.
        </p>
      </div>
    );
  }

  if (!data || data.readings.length === 0) {
    return (
      <div className="card text-center py-12">
        <div className="text-6xl mb-4">📊</div>
        <h3 className="text-xl font-serif font-semibold text-neutral-800 mb-2">
          No BP Readings Yet
        </h3>
        <p className="text-neutral-600">
          Start tracking your blood pressure to see your history here.
        </p>
      </div>
    );
  }

  const hasMore = data.total > offset + limit;
  const hasPrevious = offset > 0;

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-serif font-semibold text-neutral-800">
          Reading History
        </h2>
        <p className="text-sm text-neutral-600">
          {data.total} reading{data.total !== 1 ? 's' : ''} in the last {days} days
        </p>
      </div>

      {/* Readings List */}
      <div className="grid gap-4">
        {data.readings.map((reading) => (
          <BPReadingCard
            key={reading._id || reading.id}
            reading={reading}
            onEdit={onEdit}
            onDelete={handleDelete}
          />
        ))}
      </div>

      {/* Pagination */}
      {(hasMore || hasPrevious) && (
        <div className="flex justify-between items-center pt-4">
          <button
            onClick={() => setOffset(Math.max(0, offset - limit))}
            disabled={!hasPrevious}
            className="btn btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
          >
            ← Previous
          </button>

          <span className="text-sm text-neutral-600">
            Showing {offset + 1} - {Math.min(offset + limit, data.total)} of {data.total}
          </span>

          <button
            onClick={() => setOffset(offset + limit)}
            disabled={!hasMore}
            className="btn btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next →
          </button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="card max-w-md mx-4">
            <h3 className="text-xl font-serif font-semibold text-neutral-800 mb-4">
              Delete Reading?
            </h3>
            <p className="text-neutral-600 mb-6">
              Are you sure you want to delete this BP reading? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={cancelDelete}
                className="flex-1 btn btn-outline"
                disabled={deleteMutation.isPending}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 btn bg-red-600 text-white hover:bg-red-700"
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BPHistoryList;
